import { AsyncPipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { MatBadge } from '@angular/material/badge'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MatDialog } from '@angular/material/dialog'
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatMenuItem } from '@angular/material/menu'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatTooltip } from '@angular/material/tooltip'
import {
  DataContextBuilder,
  ElderDataToolbarComponent,
  ElderFormFieldLabelDirective,
  ElderNumberCellDirective,
  ElderQuantityInputControlComponent,
  ElderQuantityPipe,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderToastService,
  ElderToolbarContentDirective,
  ElderValidationErrorDirective,
  Filter,
  IDataContext,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { filter, map, mergeMap } from 'rxjs/operators'
import { WorkBatchPackagesFilterBuilder } from '../../../../packages/work-batch/work-batch-package-filter-builder'
import { WorkBatchPackagesService } from '../../../../../work-batch-packages.service'
import { truthyPredicate } from '../../../../../../functional.util'
import { isPackageSharingEnabled } from '../../../../../../environments/environment'
import {
  WorkBatch,
  WorkBatchSegment,
  WorkBatchSegmentPackagingMode,
  WorkBatchState,
} from '../../../../orders/batches/work-batch'
import { WorkOrderStorageLocationOverview } from '../../../../orders/work-orders/work-order'
import { OutputPreviewService } from '../../../../output/preview/output-preview.service'
import { PrintButtonComponent } from '../../../../output/print/print-button/print-button.component'
import { PrintCommandDirective } from '../../../../output/print/print-command.directive'
import { ReportSpec } from '../../../../output/report/report-job'
import { NewPackage, Package, PackageState } from '../../../../packages/package'
import {
  StorageOperation,
  StorageOperationResultType,
} from '../../../../storage/operations/storage-operation'
import { CheckIn, CheckInMode } from '../../../../storage/operations/storage-operation-request'
import { StorageOperationsService } from '../../../../storage/operations/storage-operations.service'
import {
  ITerminalPackageDialogOptions,
  TerminalBatchPackageDetailDialogComponent,
} from '../terminal-batch-package-detail-dialog/terminal-batch-package-detail-dialog.component'
import { TerminalWorkBatchCtx } from '../terminal-work-batch-ctx'
import { WorkOrderStorageLocationUtil } from '../work-order-storage-location-util'

@Component({
  selector: 'app-terminal-batch-packages',
  templateUrl: './terminal-batch-packages.component.html',
  styleUrls: ['./terminal-batch-packages.component.scss'],
  imports: [
    ElderTableComponent,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    ElderToolbarContentDirective,
    MatIconButton,
    MatBadge,
    MatIcon,
    PrintButtonComponent,
    PrintCommandDirective,
    MatButton,
    MatFormField,
    ElderFormFieldLabelDirective,
    MatLabel,
    MatInput,
    FormsModule,
    ElderQuantityInputControlComponent,
    MatError,
    ElderValidationErrorDirective,
    MatMenuItem,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    ElderNumberCellDirective,
    MatTooltip,
    AsyncPipe,
    ElderTranslatePipe,
    ElderQuantityPipe,
  ],
})
export class TerminalBatchPackagesComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  protected readonly terminalBatchCtx$ = new BehaviorSubject<TerminalWorkBatchCtx>(null)
  private readonly workBatchSegment$ = new BehaviorSubject<WorkBatchSegment>(null)

  protected readonly storageOverview$ = new BehaviorSubject<WorkOrderStorageLocationOverview>(null)

  protected readonly labelReport$: Observable<ReportSpec>

  protected readonly data: IDataContext<Package>

  protected selectedPackages: Package[]

  protected readonly isPackageSharingEnabled = isPackageSharingEnabled

  @Output()
  public storageOverviewRefreshRequest = new EventEmitter()

  private workBatchPackagesService = inject(WorkBatchPackagesService)
  private storageOperationService = inject(StorageOperationsService)
  private toastService = inject(ElderToastService)
  private outputPreviewService = inject(OutputPreviewService)
  private dialog = inject(MatDialog)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const workBatchPackageDataSource = this.workBatchPackagesService.buildDataSource()
    this.data = DataContextBuilder.start<Package>()
      .pageSize(30)
      .reloadOnLocalChanges()
      .build(workBatchPackageDataSource)

    this.terminalBatchCtx$
      .pipe(
        takeUntilDestroyed(),
        filter(truthyPredicate),
        map((terminalBatchCtx) => this.determineFilters(terminalBatchCtx))
      )
      .subscribe((packageFilters) => this.updateFilters(packageFilters))

    this.labelReport$ = this.workBatch$.pipe(
      filter(truthyPredicate),
      map((batch) => ReportSpec.packageLabelsForBatch(batch.id))
    )
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set terminalBatchCtx(ctx: TerminalWorkBatchCtx) {
    this.log.debug('Received TerminalWorkBatchCtx:', ctx)
    this.terminalBatchCtx$.next(ctx)
  }

  public get terminalBatchCtx(): TerminalWorkBatchCtx {
    return this.terminalBatchCtx$.getValue()
  }

  @Input()
  public set workBatchSegment(segment: WorkBatchSegment) {
    this.workBatchSegment$.next(segment)
  }

  public get workBatchSegment(): WorkBatchSegment {
    return this.workBatchSegment$.getValue()
  }

  @Input()
  public set storageOverview(storageOverview: WorkOrderStorageLocationOverview) {
    this.storageOverview$.next(storageOverview)
  }

  public get storageOverview(): WorkOrderStorageLocationOverview {
    return this.storageOverview$.getValue()
  }

  public get workBatch$(): Observable<WorkBatch> {
    return this.terminalBatchCtx$.pipe(map((ctx) => ctx.terminalBatch.workBatch))
  }

  public get workBatch(): WorkBatch {
    return this.terminalBatchCtx !== null ? this.terminalBatchCtx.terminalBatch.workBatch : null
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public data$(): Observable<Package[]> {
    return this.data?.data
  }

  public currentPackageCount(): number {
    return this.data?.snapshot?.total ?? 0
  }

  protected canAdd(ctx: TerminalWorkBatchCtx): boolean {
    return this.packageManagementAllowed(ctx)
  }

  protected onRequestNew(_: Event): void {
    this.workBatchPackagesService
      .createBatchPkg(NewPackage.ofWorkBatch(this.workBatch.id))
      .subscribe({
        next: (pkg) => {
          if (this.isAutoCheckInNecessary()) {
            this.checkIn([pkg.id]).subscribe({
              next: (operation) => {
                if (operation?.result?.resultType === StorageOperationResultType.FAILED) {
                  this.toastService.error('Failed to check-in package!', operation?.result?.message)
                } else {
                  this.openDetail(pkg)
                }
              },
              error: (err) => this.toastService.error('Failed to check-in package!', err),
            })
          } else {
            this.openDetail(pkg)
          }
        },
        error: (err) => this.toastService.error('Failed to create package!', err),
      })
  }

  protected onManualCheckIn(): void {
    this.checkIn(this.data.snapshot.data.map((pkg) => pkg.id)).subscribe({
      next: (operation) => {
        if (operation?.result?.resultType === StorageOperationResultType.FAILED) {
          this.toastService.error('Failed to check-in package!', operation?.result?.message)
        } else {
          this.toastService.info('Successfully checked-in packages')
        }
        this.reloadStorageOverview()
      },
      error: (err) => this.toastService.error('Failed to check-in packages!', err),
    })
  }

  protected canRemove(ctx: TerminalWorkBatchCtx): boolean {
    return this.packageManagementAllowed(ctx)
  }

  protected onRequestRemove(packages: Package[]): void {
    const pkgs = packages.filter((pkg) => pkg.state === PackageState.PREVIEW)

    if (pkgs.length > 0) {
      this.storageOperationService
        .retract(pkgs.map((pkg) => pkg.id))
        .pipe(
          mergeMap(() =>
            this.workBatchPackagesService.deleteWorkBatchPackages(
              this.terminalBatchCtx.terminalBatch.id,
              pkgs
            )
          )
        )
        .subscribe({
          next: (_) => {
            this.reloadStorageOverview()
            this.data.reload() // TODO: why is a reload needed?
            this.toastService.info('messages.deleting_successful')
          },
          error: (err) => this.toastService.error('Failed to delete package!', err),
        })
    } else {
      this.toastService.error(
        'Failed to delete package!',
        'Packages must be in state "PREVIEW" to be deleted.'
      )
    }
  }

  protected canPrintPackageLabels(ctx: TerminalWorkBatchCtx): boolean {
    return (
      ctx.terminalBatch.workBatch.state === WorkBatchState.PROCESSING &&
      !!ctx.terminal.labelPrinter?.printerId
    )
  }

  protected canPreviewPackageLabels(ctx: TerminalWorkBatchCtx): boolean {
    return ctx.terminalBatch.workBatch.state === WorkBatchState.PROCESSING
  }

  protected onPreviewPackageLabels(ctx: TerminalWorkBatchCtx): void {
    this.outputPreviewService.openPreviewDialog(
      ReportSpec.packageLabelsForBatch(ctx.terminalBatch.workBatch.id)
    )
  }

  protected openDetail(pkg: Package): void {
    if (pkg) {
      const dialogRef = this.dialog
        .open(TerminalBatchPackageDetailDialogComponent, {
          maxWidth: '100vw',
          maxHeight: '100vh',
          width: '100vw',
          height: '100vh',
          disableClose: true,
          data: <ITerminalPackageDialogOptions>{
            pkgId: pkg.id,
            terminalBatchCtx: this.terminalBatchCtx,
          },
        })
        .afterClosed()
        .subscribe(() => {
          this.data.reload('dialog closed') // in case dialog is closed without saving package, but items were updated
          this.reloadStorageOverview()
        })
    }
  }

  protected isManualCheckInNecessary(): boolean {
    return (
      this.finalizesPackaging() &&
      WorkOrderStorageLocationUtil.isManualCheckInNecessary(this.storageOverview)
    )
  }

  protected isAutoCheckInNecessary(): boolean {
    return WorkOrderStorageLocationUtil.isAutoCheckInNecessary(this.storageOverview)
  }

  protected hasPackages(): boolean {
    return !this.data.isEmpty
  }

  protected hasWeight(pkg: Package): boolean {
    return !WorkOrderStorageLocationUtil.isEmpty(pkg)
  }

  isUsedShared(pkg: Package, ctx: TerminalWorkBatchCtx): boolean {
    if (pkg.items.length === 0) {
      return false
    } else {
      const activeWorkBatches = new Set(pkg.production!.activeWorkBatches ?? [])
      const numberOfWorkBatches = activeWorkBatches.size
      const isForeign = !activeWorkBatches.has(ctx.terminalBatch.workBatch.id)
      return numberOfWorkBatches > 1 || isForeign
    }
  }

  deletionSelectionFn(ctx: TerminalWorkBatchCtx): (pkg: Package) => boolean {
    return (pkg) => this.containsNoOtherBatches(pkg, ctx) // TODO: deduplicate
  }

  containsNoOtherBatches(pkg: Package, ctx: TerminalWorkBatchCtx): boolean {
    const nonBatchBundles = pkg.items.filter(
      (bundle) => bundle.workBatchId !== ctx.terminalBatch.id
    ).length
    const containsNoOtherBatches = nonBatchBundles === 0
    return containsNoOtherBatches
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private checkIn(packages: string[]): Observable<StorageOperation | null> {
    const checkIn = new CheckIn(
      CheckInMode.PREVIEW,
      this.terminalBatchCtx.terminalBatch.workBatch.workOrderId,
      packages,
      this.storageOverview.baseLocation.path,
      undefined
    )

    return this.storageOperationService.checkIn(checkIn)
  }

  private packageManagementAllowed(ctx: TerminalWorkBatchCtx): boolean {
    return ctx.terminalBatch.workBatch.state === WorkBatchState.PROCESSING
  }

  private updateFilters(filters: Filter[]): void {
    this.log.debug('Updating filters', filters)
    if (this.data.isStarted) {
      this.data.filter.replaceFilters(filters)
    } else {
      this.data.start([], filters)
    }
  }

  private reloadStorageOverview(): void {
    this.storageOverviewRefreshRequest.emit()
  }

  private finalizesPackaging(): boolean {
    return this.workBatchSegment.packaging?.mode === WorkBatchSegmentPackagingMode.FINAL
  }

  private determineFilters(terminalBatchCtx: TerminalWorkBatchCtx): Filter[] {
    return [WorkBatchPackagesFilterBuilder.workBatchId(terminalBatchCtx.terminalBatch.workBatch.id)]
  }

  isWorkOrderRoutePackageSharingEnabled(terminalBatchCtx: TerminalWorkBatchCtx): boolean {
    return terminalBatchCtx.terminalBatch.workBatch.productionLine.packaging.packageSharingEnabled
  }
}
